/**
 * TabSelectorCard - Vertical (or horizontal) tab list that broadcasts the
 * selected tab key via a custom DOM event ('wg-tab-change') so that sibling
 * FeatureCard DetailPanel components can show/hide themselves.
 *
 * Usage in tandem with FeatureCard (panelKey prop):
 *   <TabSelectorCard defaultSelected="web" items={[{key:'web', label:'Web'}]} />
 *   <FeatureCard panelKey="web" title="Web Development" body="..." bullets={[...]} />
 */
import React, { useState, useEffect } from 'react';

interface TabItem { key: string; label: string; }

export interface TabSelectorCardProps {
  label?: string;
  defaultSelected?: string;
  items?: TabItem[];
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

const TabSelectorCard: React.FC<TabSelectorCardProps> = ({
  label = 'Tabs',
  defaultSelected,
  items = [],
  orientation = 'vertical',
  className = '',
}) => {
  const initialKey = defaultSelected || items[0]?.key || '';
  const [selected, setSelected] = useState(initialKey);

  // Broadcast the initial selection so panels initialize correctly
  useEffect(() => {
    window.dispatchEvent(new CustomEvent<string>('wg-tab-change', { detail: initialKey }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (key: string) => {
    setSelected(key);
    window.dispatchEvent(new CustomEvent<string>('wg-tab-change', { detail: key }));
  };

  const isVertical = orientation === 'vertical';

  return (
    <nav
      aria-label={label}
      className={`tab-selector-card ${className}`}
      style={{
        display: 'flex',
        flexDirection: isVertical ? 'column' : 'row',
        gap: isVertical ? 'var(--spacing-2)' : 'var(--spacing-4)',
        ...(isVertical
          ? { minWidth: '200px', paddingRight: 'var(--spacing-6)', borderRight: '1px solid var(--color-border)' }
          : { flexWrap: 'wrap', borderBottom: '1px solid var(--color-border)', paddingBottom: 'var(--spacing-4)', marginBottom: 'var(--spacing-6)' }
        ),
      }}
    >
      {items.map((item) => {
        const isActive = item.key === selected;
        return (
          <button
            key={item.key}
            onClick={() => handleSelect(item.key)}
            aria-pressed={isActive}
            style={{
              textAlign: 'left',
              padding: 'var(--spacing-3) var(--spacing-4)',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              cursor: 'pointer',
              fontWeight: isActive ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
              fontSize: 'var(--font-size-sm)',
              color: isActive ? 'var(--color-primary)' : 'var(--color-text-muted)',
              backgroundColor: isActive ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)' : 'transparent',
              transition: 'all var(--transition-fast)',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.color = 'var(--color-text)';
                e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-primary) 6%, transparent)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.color = 'var(--color-text-muted)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            {item.label}
          </button>
        );
      })}
    </nav>
  );
};

export default TabSelectorCard;
